import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { 
  User, 
  Mail, 
  Lock, 
  LogOut,
  Loader2,
  CheckCircle,
  XCircle,
  Calculator,
  Variable,
  Shapes,
  Sigma,
  Trophy,
  Target,
  BookOpen,
  Flame,
  Eye,
  EyeOff
} from "lucide-react";

const topicIcons = {
  arithmetic: { icon: Calculator, color: "icon-arithmetic", name: "Arithmetic" },
  algebra: { icon: Variable, color: "icon-algebra", name: "Algebra" },
  geometry: { icon: Shapes, color: "icon-geometry", name: "Geometry" },
  calculus: { icon: Sigma, color: "icon-calculus", name: "Calculus" },
  general: { icon: BookOpen, color: "bg-primary/10 text-primary", name: "General" }
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, updateProfile, changePassword, refreshUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [profileForm, setProfileForm] = useState({
    name: user?.name || "",
    email: user?.email || ""
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    number: false,
    special: false
  });

  const validatePassword = (password) => {
    setPasswordChecks({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await updateProfile(profileForm);
      toast.success("Profile updated successfully!");
      setEditMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!Object.values(passwordChecks).every(Boolean)) {
      toast.error("Please meet all password requirements");
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    setLoading(true);
    
    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success("Password changed successfully!");
      setShowPasswordForm(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  if (!user) {
    navigate("/auth");
    return null;
  }

  const stats = user.stats || {};
  const topicsStudied = stats.topics_studied || {};
  const totalTopicQuestions = Object.values(topicsStudied).reduce((a, b) => a + b, 0);

  const PasswordCheck = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-xs ${valid ? 'text-green-600' : 'text-muted-foreground'}`}>
      {valid ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {text}
    </div>
  );

  return (
    <div className="page-transition py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <User className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit']" data-testid="profile-title">
                {user.name}
              </h1>
              <p className="text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="rounded-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <div className="lg:col-span-2 space-y-6">
            {/* Learning Stats */}
            <Card className="border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Learning Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-xl bg-muted/50">
                    <div className="text-3xl font-bold text-primary mb-1">
                      {stats.total_questions || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Questions Asked</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-muted/50">
                    <div className="text-3xl font-bold text-primary mb-1">
                      {stats.practice_completed || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Practice Problems</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-muted/50">
                    <div className="flex items-center justify-center gap-1 text-3xl font-bold text-orange-500 mb-1">
                      <Flame className="w-6 h-6" />
                      {stats.streak_days || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Day Streak</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Topics Breakdown */}
            <Card className="border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Topics Studied
                </CardTitle>
                <CardDescription>Your question distribution by topic</CardDescription>
              </CardHeader>
              <CardContent>
                {totalTopicQuestions > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(topicsStudied).map(([topic, count]) => {
                      const topicInfo = topicIcons[topic] || topicIcons.general;
                      const Icon = topicInfo.icon;
                      const percentage = Math.round((count / totalTopicQuestions) * 100);
                      
                      return (
                        <div key={topic} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg ${topicInfo.color} flex items-center justify-center`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <span className="font-medium capitalize">{topicInfo.name}</span>
                            </div>
                            <span className="text-sm text-muted-foreground">{count} questions</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Start asking questions to see your progress!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Profile Settings */}
          <div className="space-y-6">
            {/* Edit Profile */}
            <Card className="border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        data-testid="edit-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        data-testid="edit-email"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={loading} className="flex-1" data-testid="save-profile">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setEditMode(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>{user.name}</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>{user.email}</span>
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => setEditMode(true)}
                      data-testid="edit-profile-btn"
                    >
                      Edit Profile
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Security
                </CardTitle>
              </CardHeader>
              <CardContent>
                {showPasswordForm ? (
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        required
                        data-testid="current-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={passwordForm.newPassword}
                          onChange={(e) => {
                            setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                            validatePassword(e.target.value);
                          }}
                          required
                          data-testid="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-1 mt-2">
                        <PasswordCheck valid={passwordChecks.length} text="8+ chars" />
                        <PasswordCheck valid={passwordChecks.uppercase} text="Uppercase" />
                        <PasswordCheck valid={passwordChecks.number} text="Number" />
                        <PasswordCheck valid={passwordChecks.special} text="Special" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        required
                        data-testid="confirm-password"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        type="submit" 
                        disabled={loading || !Object.values(passwordChecks).every(Boolean)}
                        className="flex-1"
                        data-testid="change-password-submit"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change"}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => setShowPasswordForm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setShowPasswordForm(true)}
                    data-testid="change-password-btn"
                  >
                    Change Password
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
